import { makeAutoObservable, observable, runInAction } from 'mobx';
import customersStore, { CustomerStoreResults } from '../../customers/store/customer-store';
import { LinerDrawingReference, LinerLocation, LinerRegion, PutAssetSectionImageDto, PutAssetSectionLinersDto, PutLinerRegion, WearReferencePlane } from '../models/asset-section-models';
import { getAssetSectionLinersLocations, putAssetSectionImage, putAssetSectionLiners, putLinerRegionsHoles, putlinersDrawingReferences, putLinersLocationWearReferencePlan } from '../services/asset-section-services';

class LinersLocationsStore {
    private customersStore;

    linersLocationsLoading: boolean = false;

    constructor() {
        this.customersStore = customersStore;
        makeAutoObservable(this);
    }

    loadLinerLocations = async (assetSectionId: string) => {
        if (this.linersLocationsLoading) {
            return;
        }

        runInAction(() => {
            this.linersLocationsLoading = true;
        });

        const data: LinerLocation[] = await getAssetSectionLinersLocations(assetSectionId);
        runInAction(() => {
            customersStore.assetSectionMap[assetSectionId].linerLocations = data.map(liner => observable.object(liner));
            data.forEach(linerlocation => {
                customersStore.linerLocationMap[linerlocation.id] = observable.object(linerlocation);
            });
           
            this.linersLocationsLoading = false;
        });
    }

    GetLinerLocationsByAssetSectionId = (id: string): (LinerLocation[]) => {
        try {
            const assetSection = customersStore.assetSectionMap[id];
            return assetSection.linerLocations ?? []
        } catch (e) {
            return [];
        }
    }

    GetLinerLocationById = (id: string): (LinerLocation|null) => {
        try {
            return customersStore.linerLocationMap[id];

        } catch (e) {
            return null;
        }
    }

    GetLinerLocationIdByReference = (assetSectionId: string, reference: string): (string | null) => {
        try {
            const linerLocations = customersStore.assetSectionMap[assetSectionId].linerLocations;
            const linerlocation = linerLocations.find(l => l.reference.toLowerCase() === reference.toLowerCase());
            return linerlocation?.id ?? null
        } catch (e) {
            return null;
        }
    }

    GetLinerRegionIdByLinerLocationRef = (linerLocationId: string, reference: string): (string|undefined) => {
        try {

            const linerLocation = customersStore.linerLocationMap[linerLocationId];
            const linerRegions = linerLocation.linerRegions ?? []
            return linerRegions.find(l => l.reference.trim().toLowerCase() == reference.toLowerCase())?.id;
        } catch (e) {
            return;
        }
    }

    GetLinerRegionByLinerRegionRef = (linerLocationId: string, reference: string): (LinerRegion | null) => {
        try {
            const linerLocation = customersStore.linerLocationMap[linerLocationId];
            const linerRegions = linerLocation.linerRegions ?? []

            return linerRegions.find(l => l.reference.trim().toLowerCase() === reference.toLowerCase()) ?? null
        } catch (e) {
            return null;
        }
    }

    GetLinerRegionIdByReference = (linerLocationId: string, reference: string): (string | undefined) => {
        try {
            const linerlocations = customersStore.linerLocationMap[linerLocationId].linerRegions;
            const linerRegion = linerlocations.find(l => l.reference.toLowerCase() === reference.toLowerCase());
            return linerRegion?.id
        } catch (e) {
            return;
        }
    }

    PutLinerLocations = async (entities: PutAssetSectionLinersDto): Promise<CustomerStoreResults> => {
        if (this.linersLocationsLoading) {
            return { passed: false, message: "", entityId: "" };
        }

        runInAction(() => {
            this.linersLocationsLoading = true;
        });

        try {
            const response = await putAssetSectionLiners(entities);
            if (response.statusCode === 200) {
                const assetSectionId = entities.id;
                this.linersLocationsLoading = false;
                this.loadLinerLocations(assetSectionId);

                return { passed: true, message: "" };
            } else {
                const message = "An error has occurred, please check the logs";
                console.error("Error editing entity:", response);
                return { passed: false, message, entityId: "" };
            }
        } catch (error) {
            console.error("Error editing entity:", error);
            return { passed: false, message: "An error has occurred, please check the logs", entityId: "" };
        } finally {
            runInAction(() => {
                this.linersLocationsLoading = false;
            });
        }
    }

    PutAssetSectionImage = async (entities: PutAssetSectionImageDto): Promise<CustomerStoreResults> => {
        if (this.linersLocationsLoading) {
            return { passed: false, message: "", entityId: "" };
        }

        runInAction(() => {
            this.linersLocationsLoading = true;
        });

        try {
            const response = await putAssetSectionImage(entities);
            if (response.statusCode === 200) {
                return { passed: true, message: "" };
            } else {
                const message = "An error has occurred, please check the logs";
                console.error("Error upload Asset Image:", response);
                return { passed: false, message, entityId: "" };
            }
        } catch (error) {
            console.error("Error editing entity:", error);
            return { passed: false, message: "An error has occurred, please check the logs", entityId: "" };
        } finally {
            runInAction(() => {
                this.linersLocationsLoading = false;
            });
        }
    }

    EditLinerLocationsForWearReferencePlane = async (assetSectionId: string, entities: WearReferencePlane[]): Promise<CustomerStoreResults> => {
        if (this.linersLocationsLoading) {
            return { passed: false, message: "", entityId: "" };
        }

        runInAction(() => {
            this.linersLocationsLoading = true;
        });
        try {
            // Add new function here
            const filterData = entities.filter(e => e.linerLocationId)

            const response = await putLinersLocationWearReferencePlan(filterData);

            if (response.statusCode === 200) {

                this.linersLocationsLoading = false;

                this.loadLinerLocations(assetSectionId);

                return { passed: true, message: "" };
            } else {
                const message = "An error has occurred, please check the logs";
                console.error("Error editing entity:", response);
                return { passed: false, message, entityId: "" };
            }
        } catch (error) {
            console.error("Error editing entity:", error);
            return { passed: false, message: "An error has occurred, please check the logs", entityId: "" };
        } finally {
            runInAction(() => {
                this.linersLocationsLoading = false;
            });
        }
    }

    PutAssetSectionHoles = async (assetSectionId: string, entities: PutLinerRegion[]): Promise<CustomerStoreResults> => {
        if (this.linersLocationsLoading) {
            return { passed: false, message: "", entityId: "" };
        }

        runInAction(() => {
            this.linersLocationsLoading = true;
        });
        const filterData = entities.filter(e => e.linerLocationId);
 
        try {
            const response = await putLinerRegionsHoles(filterData);
            if (response.statusCode === 200) {
                this.linersLocationsLoading = false;

                this.loadLinerLocations(assetSectionId);

                return { passed: true, message: "" };
            } else {
                const message = "An error has occurred, please check the logs";
                console.error("Error editing entity:", response);
                return { passed: false, message, entityId: "" };
            }
        } catch (error) {
            console.error("Error editing entity:", error);
            return { passed: false, message: "An error has occurred, please check the logs", entityId: "" };
        } finally {
            runInAction(() => {
                this.linersLocationsLoading = false;
            });
        }
    }

    EditLinerLocationsDrawingReference = async (assetSectionId: string, entities: LinerDrawingReference[]): Promise<CustomerStoreResults> => {

        if (this.linersLocationsLoading) {
            return { passed: false, message: "", entityId: "" };
        }

        runInAction(() => {
            this.linersLocationsLoading = true;
        });
        try {
            // Add new function here
            const filterData = entities.filter(e => e.linerLocationId);

            const response = await putlinersDrawingReferences(filterData);

            if (response.statusCode === 200) {
                this.linersLocationsLoading = false;
                this.loadLinerLocations(assetSectionId);

                return { passed: true, message: "" };
            } else {
                const message = "An error has occurred, please check the logs";
                console.error("Error editing entity:", response);
                return { passed: false, message, entityId: "" };
            }
        } catch (error) {
            console.error("Error editing entity:", error);
            return { passed: false, message: "An error has occurred, please check the logs", entityId: "" };
        } finally {
            runInAction(() => {
                this.linersLocationsLoading = false;
            });
        }
    }
}

export default new LinersLocationsStore();